import { cn } from '@/lib/utils';
import { useId, type ReactNode } from 'react';

import PortalTooltip from './PortalTooltip';

/**
 * Top-center floating toolbar used by sketch / picking tools (stockpile,
 * add-surface, elevation-profile draw). Renders a status pill above an
 * action-button row, both with backdrop-blur cards consistent with the
 * rest of the viewer chrome.
 *
 * Extracted from `StockpileToolbar` and `AddSurfaceToolbar` which had
 * pixel-identical layouts and near-identical inner button components —
 * the only intentional difference is the primary-action color (orange
 * for stockpile, blue elsewhere). That's exposed via the `primaryClass`
 * prop on `<FloatingToolbarButton>`.
 *
 * Layout:
 *   <FloatingToolbar status="…">
 *     <FloatingToolbarButton ... />
 *     <FloatingToolbarSeparator />
 *     <FloatingToolbarButton intent="primary" ... />
 *     <FloatingToolbarButton intent="danger" ... />
 *   </FloatingToolbar>
 *
 * `status` is optional — pass `undefined` to render only the action row.
 */
export const FloatingToolbar = ({
  status,
  children,
  className,
}: {
  status?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      'absolute z-20 top-4 left-1/2 -translate-x-1/2 pointer-events-auto flex flex-col items-center gap-2',
      className,
    )}>
    {status != null && (
      <div className='px-3 py-1.5 rounded-md bg-white/90 dark:bg-stone-900/90 backdrop-blur-md border border-slate-200/70 dark:border-stone-700/70 text-sm text-slate-700 dark:text-stone-100 shadow-sm'>
        {status}
      </div>
    )}
    <div className='flex gap-1 bg-white/90 dark:bg-stone-900/90 backdrop-blur-md border border-slate-200/70 dark:border-stone-700/70 rounded-md p-1 shadow-sm'>
      {children}
    </div>
  </div>
);

export const FloatingToolbarSeparator = () => (
  <div className='w-px bg-slate-200/70 dark:bg-stone-700/70 mx-1' />
);

/**
 * A single 32×32 toolbar button with portal-tooltip and intent variants.
 *
 * Variants:
 *   - default      → neutral text + hover bg
 *   - active       → filled (slate/white) toggle pill
 *   - intent='primary' → filled with `primaryClass` (caller-supplied color)
 *   - intent='danger'  → red foreground, soft red hover bg
 *
 * `active` is ignored when an intent is set so callers don't have to think
 * about precedence.
 */
export const FloatingToolbarButton = ({
  icon,
  tooltip,
  onClick,
  disabled,
  active,
  intent,
  primaryClass = 'bg-blue-600 hover:bg-blue-700',
}: {
  icon: ReactNode;
  tooltip: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  intent?: 'primary' | 'danger';
  /**
   * Tailwind classes used for the `intent='primary'` background. Lets each
   * tool keep its brand color (stockpile → orange, surface → blue, etc.)
   * without forking the whole component.
   */
  primaryClass?: string;
}) => {
  const id = useId();
  return (
    <>
      <button
        data-tooltip-id={id}
        // Stop propagation so toolbar clicks don't reach DOM listeners
        // attached to the canvas (the line / polygon picker click
        // handlers, GeoJson feature-select, etc.). The toolbar is a
        // sibling of the Canvas in the layout but the click event still
        // bubbles up through document and any ancestor handlers can
        // see it; without this stop the OK / Cancel button could
        // inadvertently trigger a vertex pick or feature select.
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        // Pointerdown bubbles independently of click — also stop it so
        // a stray window-level pointerup listener (e.g. an in-flight
        // vertex drag) can't pair with this pointerdown and treat the
        // toolbar press as a drag commit.
        onPointerDown={(e) => e.stopPropagation()}
        disabled={disabled}
        className={cn(
          'size-8 rounded flex items-center justify-center [&>svg]:size-4 [&>svg]:stroke-2 disabled:opacity-40 transition-colors',
          intent === 'primary' &&
            cn(primaryClass, 'text-white disabled:bg-slate-200 dark:disabled:bg-stone-800'),
          intent === 'danger' && 'text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30',
          !intent && active && 'bg-slate-800 text-white dark:bg-white dark:text-stone-700',
          !intent &&
            !active &&
            'text-slate-700 hover:bg-slate-100 dark:text-stone-100 dark:hover:bg-stone-800',
        )}>
        {icon}
      </button>
      <PortalTooltip id={id} delayHide={0} delayShow={120}>
        {tooltip}
      </PortalTooltip>
    </>
  );
};
